import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Building2,
  FolderGit2,
  FileCode,
  Image as ImageIcon,
  Calendar,
  MessageSquare,
  Users,
  Star,
  HelpCircle,
  BarChart3,
  Settings,
  Sparkles,
  ShieldCheck,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Pages & Builder', href: '/admin/pages', icon: FileText },
  { name: 'Services', href: '/admin/services', icon: Briefcase },
  { name: 'Industries', href: '/admin/industries', icon: Building2 },
  { name: 'Portfolio', href: '/admin/portfolio', icon: FolderGit2 },
  { name: 'Blog System', href: '/admin/blog', icon: FileCode },
  { name: 'Media Library', href: '/admin/media', icon: ImageIcon },
  { name: 'Bookings', href: '/admin/bookings', icon: Calendar, badge: 'New' },
  { name: 'Contact Messages', href: '/admin/messages', icon: MessageSquare },
  { name: 'Team Members', href: '/admin/team', icon: Users },
  { name: 'Testimonials', href: '/admin/testimonials', icon: Star },
  { name: 'FAQs', href: '/admin/faqs', icon: HelpCircle },
  { name: 'Navigation', href: '/admin/navigation', icon: Menu },
  { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { name: 'AI Copilot', href: '/admin/ai-assistant', icon: Sparkles, highlight: true },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
  { name: 'Users & Roles', href: '/admin/users', icon: UserCheck },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: ShieldCheck },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const { theme } = useTheme();
  const sidebarRef = useRef(null);

  // Close mobile sidebar when route changes
  useEffect(() => {
    if (mobileOpen) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile sidebar on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileOpen && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onMobileClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen, onMobileClose]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const sidebarClasses = `fixed top-0 left-0 z-40 h-screen transition-all duration-300 border-r ${
    theme === 'dark'
      ? 'bg-slate-950 border-slate-800 text-slate-200'
      : 'bg-white border-slate-200 text-slate-800'
  }`;

  const navContent = (
    <>
      {/* Brand Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800/60">
        <Link to="/admin/dashboard" className="flex items-center space-x-3 overflow-hidden">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-500 to-cyan-400 text-white font-bold text-lg shadow-lg shadow-blue-500/20 flex-shrink-0">
            F
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-extrabold text-base tracking-tight text-white">FAJTech</span>
              <span className="text-[10px] font-semibold tracking-wider text-blue-400 uppercase">Enterprise CMS</span>
            </div>
          )}
        </Link>
        {/* Desktop collapse toggle – hidden on mobile */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:block p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        {/* Mobile close button – hidden on desktop */}
        <button
          onClick={onMobileClose}
          className="lg:hidden p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Navigation items */}
      <div className="px-3 py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-4rem)] custom-scrollbar">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = item.icon;

          return (
            <Link
              key={item.name}
              to={item.href}
              title={collapsed ? item.name : ''}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl font-medium text-sm transition-all group ${
                isActive
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : item.highlight
                  ? 'bg-gradient-to-r from-purple-900/40 to-indigo-900/40 text-purple-300 hover:text-white border border-purple-500/30'
                  : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-100'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon
                  className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                    isActive ? 'text-white' : item.highlight ? 'text-purple-400' : 'text-slate-400 group-hover:text-white'
                  }`}
                />
                {!collapsed && <span>{item.name}</span>}
              </div>
              {!collapsed && item.badge && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`${sidebarClasses} hidden lg:block ${collapsed ? 'w-20' : 'w-64'}`}
      >
        {navContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={onMobileClose}
          />
          {/* Sidebar drawer */}
          <aside
            ref={sidebarRef}
            className={`${sidebarClasses} w-64 shadow-2xl animate-in slide-in-from-left duration-200`}
            style={{ zIndex: 51 }}
          >
            {navContent}
          </aside>
        </div>
      )}
    </>
  );
}
